import React, { useRef, useState } from "react";

import styles from "./Proppanel.module.css";
import { GetIndexInArray } from "../Centerpanel.tsx";
import { RouteAction, RouteActionKind } from "../../../Reducer.ts";
import { RouteState } from "../../../api/edits/editRoute.responses.ts";
import { UploadMediaType } from "../../../api/edits/upload.service.ts";
import { useUploadFile } from "../../../hooks/useUploadFile.ts";
import { getExtFromMimeType, slugify } from "../../../utils/uploadUtils.ts";
import { Input } from "../../ui/Input";
import { Label } from "../../ui/Label";
import { Button } from "../../ui/Button";
import { Separator } from "../../ui/Separator";
import { Textarea } from "../../ui/Textarea";
import { ScrollArea } from "../../ui/ScrollArea";
import { CheckCircle2, Upload, XCircle } from "lucide-react";

interface ProppanelProps {
  select: string;
  routeState: RouteState;
  routeDispatch: React.Dispatch<RouteAction>;
  currentLanguage: string;
}

// ─── Shared primitives ────────────────────────────────────────────────────────

function SectionHeader({ title }: { title: string }) {
  return (
    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground pb-1">
      {title}
    </p>
  );
}

function ReadOnlyRow({
  label,
  value,
}: {
  label: string;
  value: string | number;
}) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Input value={String(value)} readOnly className="h-8 text-sm opacity-60" />
    </div>
  );
}

function FieldRow({
  label,
  value,
  onChange,
  onBlur,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  onBlur: () => void;
  type?: string;
}) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        className="h-8 text-sm"
      />
    </div>
  );
}

function FileFieldRow({
  label,
  value,
  onChange,
  routeId,
  mediaType,
  generateFilename,
  languageCode,
  onUploadComplete,
  accept,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  routeId: string;
  mediaType: UploadMediaType;
  generateFilename: (file: File) => string;
  languageCode?: string;
  onUploadComplete: (filename: string, uploadSessionId: string) => void;
  accept?: string;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadFile, uploading, uploadError, uploadSuccess } = useUploadFile();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const filename = generateFilename(file);
    try {
      const result = await uploadFile(routeId, file, mediaType, filename, languageCode);
      onChange(result.filename);
      onUploadComplete(result.filename, result.uploadSessionId);
    } catch {
      // uploadError is set by the hook
    }
  };

  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Input
        type="text"
        value={value}
        readOnly
        className="h-8 text-sm opacity-60"
      />
      <input
        type="file"
        ref={fileInputRef}
        accept={accept}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      <Button
        variant="outline"
        size="sm"
        className="w-full gap-2"
        disabled={uploading}
        onClick={() => fileInputRef.current?.click()}
      >
        {uploading ? (
          <>
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
            Uploading…
          </>
        ) : (
          <>
            <Upload className="h-4 w-4" />
            Upload file
          </>
        )}
      </Button>
      {uploadSuccess && !uploading && (
        <p className="text-xs text-green-600 flex items-center gap-1">
          <CheckCircle2 className="h-3 w-3" /> Uploaded
        </p>
      )}
      {uploadError && !uploading && (
        <p className="text-xs text-destructive flex items-center gap-1">
          <XCircle className="h-3 w-3" /> {uploadError}
        </p>
      )}
    </div>
  );
}

function TextareaRow({
  label,
  value,
  onChange,
  onBlur,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  onBlur: () => void;
}) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        className="text-sm min-h-[80px] bg-white dark:bg-white text-zinc-900 dark:text-zinc-900"
      />
    </div>
  );
}

// ─── Route ───────────────────────────────────────────────────────────────────

function RoutePropsPanel({
  routeState,
  routeDispatch,
  currentLanguage,
}: {
  routeState: RouteState;
  routeDispatch: React.Dispatch<RouteAction>;
  currentLanguage: string;
}) {
  const translation = routeState.routeTranslations[currentLanguage];

  const [appleId, setAppleId] = useState(routeState.appleStoreId);
  const [playId, setPlayId] = useState(routeState.playStoreId);
  const [duration, setDuration] = useState(routeState.timeDuration);
  const [price, setPrice] = useState(routeState.priceInEuros);
  const distance = String(routeState.distanceInMeters);
  const [previewImg, setPreviewImg] = useState(routeState.previewImagePath);
  const [name, setName] = useState(translation?.name ?? "");
  const [description, setDescription] = useState(
    translation?.description ?? ""
  );

  return (
    <div className="space-y-4 p-3">
      <SectionHeader title="Route" />
      <FieldRow
        label="Apple Store ID"
        value={appleId}
        onChange={setAppleId}
        onBlur={() =>
          routeDispatch({
            type: RouteActionKind.CHANGE_ROUTE_APPLE_ID,
            payload: { str: appleId },
          })
        }
      />
      <FieldRow
        label="Play Store ID"
        value={playId}
        onChange={setPlayId}
        onBlur={() =>
          routeDispatch({
            type: RouteActionKind.CHANGE_ROUTE_PLAY_ID,
            payload: { str: playId },
          })
        }
      />
      <FieldRow
        label="Time Duration"
        value={duration}
        onChange={setDuration}
        onBlur={() =>
          routeDispatch({
            type: RouteActionKind.CHANGE_ROUTE_TIME_DURATION,
            payload: { str: duration },
          })
        }
      />
      <FieldRow
        label="Price (€)"
        value={price}
        onChange={setPrice}
        onBlur={() =>
          routeDispatch({
            type: RouteActionKind.CHANGE_ROUTE_PRICE,
            payload: { str: price },
          })
        }
      />
      <ReadOnlyRow label="Distance (m)" value={distance} />
      <FileFieldRow
        label="Preview Image Path"
        value={previewImg}
        onChange={setPreviewImg}
        routeId={routeState.id}
        mediaType={UploadMediaType.IMAGE}
        accept="image/*"
        generateFilename={(f) => `preview.${getExtFromMimeType(f.type)}`}
        onUploadComplete={(filename, sessionId) => {
          routeDispatch({
            type: RouteActionKind.CHANGE_ROUTE_PREVIEW_IMAGE_PATH,
            payload: { str: filename },
          });
          routeDispatch({
            type: RouteActionKind.ADD_PENDING_UPLOAD,
            payload: { uploadSessionId: sessionId, mediaType: UploadMediaType.IMAGE, filename },
          });
        }}
      />
      <Separator />
      <SectionHeader title={`Translation — ${currentLanguage}`} />
      <FieldRow
        label="Route Name"
        value={name}
        onChange={setName}
        onBlur={() =>
          routeDispatch({
            type: RouteActionKind.CHANGE_ROUTE_NAME,
            payload: { newValue: name, language: currentLanguage },
          })
        }
      />
      <TextareaRow
        label="Description"
        value={description}
        onChange={setDescription}

        onBlur={() =>
          routeDispatch({
            type: RouteActionKind.CHANGE_ROUTE_DESCRIPTION,
            payload: { newValue: description, language: currentLanguage },
          })
        }
      />
    </div>
  );
}

// ─── Sponsor (read-only) ──────────────────────────────────────────────────────

function SponsorPanel({ routeState }: { routeState: RouteState }) {
  const { id, name, imagePath } = routeState.sponsor;
  return (
    <div className="space-y-4 p-3">
      <SectionHeader title="Sponsor" />
      <ReadOnlyRow label="ID" value={id} />
      <ReadOnlyRow label="Name" value={name} />
      <ReadOnlyRow label="Image Path" value={imagePath} />
    </div>
  );
}

// ─── City (read-only) ─────────────────────────────────────────────────────────

function CityPanel({ routeState }: { routeState: RouteState }) {
  const { id, name, countryId } = routeState.city;
  return (
    <div className="space-y-4 p-3">
      <SectionHeader title="City" />
      <ReadOnlyRow label="ID" value={id} />
      <ReadOnlyRow label="Name" value={name} />
      <ReadOnlyRow label="Country ID" value={countryId} />
    </div>
  );
}

// ─── Translations list (read-only) ────────────────────────────────────────────

function TranslationsListPanel({ routeState }: { routeState: RouteState }) {
  const entries = Object.entries(routeState.routeTranslations);
  return (
    <div className="space-y-3 p-3">
      <SectionHeader title={`Translations (${entries.length})`} />
      {entries.map(([code, t]) => (
        <div
          key={code}
          className="rounded-md border border-border bg-card px-3 py-2 space-y-0.5"
        >
          <p className="text-sm font-medium">{t.languageName}</p>
          <p className="text-xs text-muted-foreground">{code}</p>
        </div>
      ))}
    </div>
  );
}

// ─── Specific translation (read-only) ────────────────────────────────────────

function TranslationPanel({
  lang,
  routeState,
}: {
  lang: string;
  routeState: RouteState;
}) {
  const t = routeState.routeTranslations[lang];
  if (!t) return null;
  return (
    <div className="space-y-4 p-3">
      <SectionHeader title="Route Translation" />
      <ReadOnlyRow label="ID" value={t.id} />
      <ReadOnlyRow label="Language" value={t.languageName} />
      <ReadOnlyRow label="Name" value={t.name} />
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">Description</Label>
        <Textarea
          value={t.description}
          readOnly
          className="text-sm min-h-[80px] opacity-60 bg-white dark:bg-white text-zinc-900 dark:text-zinc-900"
        />
      </div>
    </div>
  );
}

// ─── POIs list ────────────────────────────────────────────────────────────────

function POIsListPanel({
  routeState,
  currentLanguage,
}: {
  routeState: RouteState;
  currentLanguage: string;
}) {
  const pois = routeState.pointsOfInterest;
  return (
    <div className="space-y-3 p-3">
      <SectionHeader title={`Points of Interest (${pois.length})`} />
      {pois.map((poi) => {
        const name =
          poi.pointOfInterestTranslations[currentLanguage]?.name ?? "—";
        return (
          <div
            key={poi.id}
            className="rounded-md border border-border bg-card px-3 py-2 space-y-0.5"
          >
            <p className="text-sm font-medium">
              #{poi.pointOrder} — {name}
            </p>
            <p className="text-xs text-muted-foreground">ID: {poi.id}</p>
          </div>
        );
      })}
    </div>
  );
}

// ─── Single POI ───────────────────────────────────────────────────────────────

function POIPanel({
  no,
  routeState,
  routeDispatch,
  currentLanguage,
}: {
  no: number;
  routeState: RouteState;
  routeDispatch: React.Dispatch<RouteAction>;
  currentLanguage: string;
}) {
  const poi = routeState.pointsOfInterest[no];
  const translation = poi.pointOfInterestTranslations[currentLanguage];

  const [name, setName] = useState(translation?.name ?? "");
  const [lat, setLat] = useState(String(poi.latitude));
  const [lon, setLon] = useState(String(poi.longitude));
  const [order, setOrder] = useState(String(poi.pointOrder));
  const [radius, setRadius] = useState(String(poi.radius));
  const [audioPath, setAudioPath] = useState(translation?.audioPath ?? "");
  const [imagePath, setImagePath] = useState(poi.imagePath);
  const [text, setText] = useState(translation?.text ?? "");

  return (
    <div className="space-y-4 p-3">
      <SectionHeader title="Point of Interest" />
      <ReadOnlyRow label="ID" value={poi.id} />
      <FieldRow
        label="Name"
        value={name}
        onChange={setName}
        onBlur={() =>
          routeDispatch({
            type: RouteActionKind.CHANGE_POI_NAME,
            payload: { id: no, newValue: name, language: currentLanguage },
          })
        }
      />
      <FieldRow
        label="Latitude"
        value={lat}
        onChange={setLat}
        type="number"
        onBlur={() =>
          routeDispatch({
            type: RouteActionKind.CHANGE_POI_LATITUDE,
            payload: { id: no, num: Number(lat) },
          })
        }
      />
      <FieldRow
        label="Longitude"
        value={lon}
        onChange={setLon}
        type="number"
        onBlur={() =>
          routeDispatch({
            type: RouteActionKind.CHANGE_POI_LONGITUDE,
            payload: { id: no, num: Number(lon) },
          })
        }
      />
      <FieldRow
        label="Point Order"
        value={order}
        onChange={setOrder}
        type="number"
        onBlur={() =>
          routeDispatch({
            type: RouteActionKind.CHANGE_POI_POINT_ORDER,
            payload: { id: no, num: Number(order) },
          })
        }
      />
      <FieldRow
        label="Radius"
        value={radius}
        onChange={setRadius}
        type="number"
        onBlur={() =>
          routeDispatch({
            type: RouteActionKind.CHANGE_POI_RADIUS,
            payload: { id: no, num: Number(radius) },
          })
        }
      />
      <FileFieldRow
        label="Audio Path"
        value={audioPath}
        onChange={setAudioPath}
        routeId={routeState.id}
        mediaType={UploadMediaType.POI_AUDIO}
        languageCode={currentLanguage}
        accept="audio/*"
        generateFilename={(f) =>
          `${String(poi.pointOrder).padStart(2, '0')}.${getExtFromMimeType(f.type)}`
        }
        onUploadComplete={(filename, sessionId) => {
          routeDispatch({
            type: RouteActionKind.CHANGE_POI_AUDIO_PATH,
            payload: { id: no, newValue: filename, language: currentLanguage },
          });
          routeDispatch({
            type: RouteActionKind.ADD_PENDING_UPLOAD,
            payload: {
              uploadSessionId: sessionId,
              mediaType: UploadMediaType.POI_AUDIO,
              filename,
              languageCode: currentLanguage,
            },
          });
        }}
      />
      <FileFieldRow
        label="Image Path"
        value={imagePath}
        onChange={setImagePath}
        routeId={routeState.id}
        mediaType={UploadMediaType.IMAGE}
        accept="image/*"
        generateFilename={(f) =>
          `${String(poi.pointOrder).padStart(2, '0')}.${getExtFromMimeType(f.type)}`
        }
        onUploadComplete={(filename, sessionId) => {
          routeDispatch({
            type: RouteActionKind.CHANGE_POI_IMAGE_PATH,
            payload: { id: no, str: filename },
          });
          routeDispatch({
            type: RouteActionKind.ADD_PENDING_UPLOAD,
            payload: {
              uploadSessionId: sessionId,
              mediaType: UploadMediaType.IMAGE,
              filename,
            },
          });
        }}
      />
      <TextareaRow
        label="Description"
        value={text}
        onChange={setText}
        onBlur={() =>
          routeDispatch({
            type: RouteActionKind.CHANGE_POI_TEXT,
            payload: { id: no, newValue: text, language: currentLanguage },
          })
        }
      />
    </div>
  );
}

// ─── Background sounds list ───────────────────────────────────────────────────

function BackgroundSoundsListPanel({
  routeState,
}: {
  routeState: RouteState;
}) {
  const sounds = routeState.backgroundSounds;
  return (
    <div className="space-y-3 p-3">
      <SectionHeader title={`Background Sounds (${sounds.length})`} />
      {sounds.map((s) => (
        <div
          key={s.id}
          className="rounded-md border border-border bg-card px-3 py-2 space-y-0.5"
        >
          <p className="text-sm font-medium">{s.name}</p>
          <p className="text-xs text-muted-foreground">ID: {s.id}</p>
        </div>
      ))}
    </div>
  );
}

// ─── Single background sound ──────────────────────────────────────────────────

function BackgroundSoundPanel({
  no,
  routeState,
  routeDispatch,
}: {
  no: number;
  routeState: RouteState;
  routeDispatch: React.Dispatch<RouteAction>;
}) {
  const s = routeState.backgroundSounds[no];

  const [name, setName] = useState(s.name);
  const [lat, setLat] = useState(String(s.latitude));
  const [lon, setLon] = useState(String(s.longitude));
  const [audioPath, setAudioPath] = useState(s.audioPath);
  const [radius, setRadius] = useState(String(s.radius));
  const [minVol, setMinVol] = useState(String(s.minVolume));
  const [maxVol, setMaxVol] = useState(String(s.maxVolume));

  return (
    <div className="space-y-4 p-3">
      <SectionHeader title="Background Sound" />
      <ReadOnlyRow label="ID" value={s.id} />
      <FieldRow
        label="Name"
        value={name}
        onChange={setName}
        onBlur={() =>
          routeDispatch({
            type: RouteActionKind.CHANGE_BACKGROUND_NAME,
            payload: { id: no, str: name },
          })
        }
      />
      <FieldRow
        label="Latitude"
        value={lat}
        onChange={setLat}
        type="number"
        onBlur={() =>
          routeDispatch({
            type: RouteActionKind.CHANGE_BACKGROUND_LATITUDE,
            payload: { id: no, num: Number(lat) },
          })
        }
      />
      <FieldRow
        label="Longitude"
        value={lon}
        onChange={setLon}
        type="number"
        onBlur={() =>
          routeDispatch({
            type: RouteActionKind.CHANGE_BACKGROUND_LONGITUDE,
            payload: { id: no, num: Number(lon) },
          })
        }
      />
      <FileFieldRow
        label="Audio Path"
        value={audioPath}
        onChange={setAudioPath}
        routeId={routeState.id}
        mediaType={UploadMediaType.AMBIENT}
        accept="audio/*"
        generateFilename={(f) => `${slugify(name)}.${getExtFromMimeType(f.type)}`}
        onUploadComplete={(filename, sessionId) => {
          routeDispatch({
            type: RouteActionKind.CHANGE_BACKGROUND_AUDIO_PATH,
            payload: { id: no, str: filename },
          });
          routeDispatch({
            type: RouteActionKind.ADD_PENDING_UPLOAD,
            payload: {
              uploadSessionId: sessionId,
              mediaType: UploadMediaType.AMBIENT,
              filename,
            },
          });
        }}
      />
      <FieldRow
        label="Radius"
        value={radius}
        onChange={setRadius}
        type="number"
        onBlur={() =>
          routeDispatch({
            type: RouteActionKind.CHANGE_BACKGROUND_RADIUS,
            payload: { id: no, num: Number(radius) },
          })
        }
      />
      <FieldRow
        label="Min Volume"
        value={minVol}
        onChange={setMinVol}
        type="number"
        onBlur={() =>
          routeDispatch({
            type: RouteActionKind.CHANGE_BACKGROUND_MIN_VOLUME,
            payload: { id: no, num: Number(minVol) },
          })
        }
      />
      <FieldRow
        label="Max Volume"
        value={maxVol}
        onChange={setMaxVol}
        type="number"
        onBlur={() =>
          routeDispatch({
            type: RouteActionKind.CHANGE_BACKGROUND_MAX_VOLUME,
            payload: { id: no, num: Number(maxVol) },
          })
        }
      />
    </div>
  );
}

// ─── Focused sounds list ──────────────────────────────────────────────────────

function FocusedSoundsListPanel({ routeState }: { routeState: RouteState }) {
  const sounds = routeState.focusedSounds;
  return (
    <div className="space-y-3 p-3">
      <SectionHeader title={`Focused Sounds (${sounds.length})`} />
      {sounds.map((s) => (
        <div
          key={s.id}
          className="rounded-md border border-border bg-card px-3 py-2 space-y-0.5"
        >
          <p className="text-sm font-medium">{s.name}</p>
          <p className="text-xs text-muted-foreground">ID: {s.id}</p>
        </div>
      ))}
    </div>
  );
}

// ─── Single focused sound ─────────────────────────────────────────────────────

function FocusedSoundPanel({
  no,
  routeState,
  routeDispatch,
}: {
  no: number;
  routeState: RouteState;
  routeDispatch: React.Dispatch<RouteAction>;
}) {
  const s = routeState.focusedSounds[no];

  const [name, setName] = useState(s.name);
  const [lat, setLat] = useState(String(s.latitude));
  const [lon, setLon] = useState(String(s.longitude));
  const [audioPath, setAudioPath] = useState(s.audioPath);
  const [radius, setRadius] = useState(String(s.radius));
  const [minVol, setMinVol] = useState(String(s.minVolume));
  const [maxVol, setMaxVol] = useState(String(s.maxVolume));
  const [falloffName, setFalloffName] = useState(s.falloffName);

  return (
    <div className="space-y-4 p-3">
      <SectionHeader title="Focused Sound" />
      <ReadOnlyRow label="ID" value={s.id} />
      <FieldRow
        label="Name"
        value={name}
        onChange={setName}
        onBlur={() =>
          routeDispatch({
            type: RouteActionKind.CHANGE_FOCUSED_NAME,
            payload: { id: no, str: name },
          })
        }
      />
      <FieldRow
        label="Latitude"
        value={lat}
        onChange={setLat}
        type="number"
        onBlur={() =>
          routeDispatch({
            type: RouteActionKind.CHANGE_FOCUSED_LATITUDE,
            payload: { id: no, num: Number(lat) },
          })
        }
      />
      <FieldRow
        label="Longitude"
        value={lon}
        onChange={setLon}
        type="number"
        onBlur={() =>
          routeDispatch({
            type: RouteActionKind.CHANGE_FOCUSED_LONGITUDE,
            payload: { id: no, num: Number(lon) },
          })
        }
      />
      <FileFieldRow
        label="Audio Path"
        value={audioPath}
        onChange={setAudioPath}
        routeId={routeState.id}
        mediaType={UploadMediaType.AMBIENT}
        accept="audio/*"
        generateFilename={(f) => `${slugify(name)}.${getExtFromMimeType(f.type)}`}
        onUploadComplete={(filename, sessionId) => {
          routeDispatch({
            type: RouteActionKind.CHANGE_FOCUSED_AUDIO_PATH,
            payload: { id: no, str: filename },
          });
          routeDispatch({
            type: RouteActionKind.ADD_PENDING_UPLOAD,
            payload: {
              uploadSessionId: sessionId,
              mediaType: UploadMediaType.AMBIENT,
              filename,
            },
          });
        }}
      />
      <FieldRow
        label="Radius"
        value={radius}
        onChange={setRadius}
        type="number"
        onBlur={() =>
          routeDispatch({
            type: RouteActionKind.CHANGE_FOCUSED_RADIUS,
            payload: { id: no, num: Number(radius) },
          })
        }
      />
      <FieldRow
        label="Min Volume"
        value={minVol}
        onChange={setMinVol}
        type="number"
        onBlur={() =>
          routeDispatch({
            type: RouteActionKind.CHANGE_FOCUSED_MIN_VOLUME,
            payload: { id: no, num: Number(minVol) },
          })
        }
      />
      <FieldRow
        label="Max Volume"
        value={maxVol}
        onChange={setMaxVol}
        type="number"
        onBlur={() =>
          routeDispatch({
            type: RouteActionKind.CHANGE_FOCUSED_MAX_VOLUME,
            payload: { id: no, num: Number(maxVol) },
          })
        }
      />
      <Separator />
      <SectionHeader title="Falloff" />
      <ReadOnlyRow label="Falloff ID" value={s.falloffId} />
      <FieldRow
        label="Falloff Name"
        value={falloffName}
        onChange={setFalloffName}
        onBlur={() =>
          routeDispatch({
            type: RouteActionKind.CHANGE_FOCUSED_FALLOF_NAME,
            payload: { id: no, str: falloffName },
          })
        }
      />
    </div>
  );
}

// ─── Route line list ──────────────────────────────────────────────────────────

function RouteLineListPanel({ routeState }: { routeState: RouteState }) {
  const points = routeState.linePoints;
  return (
    <div className="space-y-3 p-3">
      <SectionHeader title={`Route Line (${points.length} points)`} />
      {points.map((p, i) => (
        <div
          key={i}
          className="rounded-md border border-border bg-card px-3 py-2 space-y-0.5"
        >
          <p className="text-sm font-medium">Point #{i}</p>
          <p className="text-xs text-muted-foreground">
            {p.latitude.toFixed(6)}, {p.longitude.toFixed(6)}
          </p>
        </div>
      ))}
    </div>
  );
}

// ─── Single route line point (read-only) ─────────────────────────────────────

function RouteLinePointPanel({
  no,
  routeState,
}: {
  no: number;
  routeState: RouteState;
}) {
  const p = routeState.linePoints[no];
  if (!p) return null;
  return (
    <div className="space-y-4 p-3">
      <SectionHeader title="Route Line Point" />
      <ReadOnlyRow label="Index" value={no} />
      <ReadOnlyRow label="Latitude" value={p.latitude} />
      <ReadOnlyRow label="Longitude" value={p.longitude} />
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export const Proppanel = ({
  select,
  routeState,
  routeDispatch,
  currentLanguage,
}: ProppanelProps) => {
  function renderPanel() {
    // rprop variants
    if (select === "rpropSpon") {
      return <SponsorPanel routeState={routeState} />;
    }
    if (select === "rpropCity") {
      return <CityPanel routeState={routeState} />;
    }
    if (select.startsWith("rpropTran")) {
      if (select === "rpropTran") {
        return <TranslationsListPanel routeState={routeState} />;
      }
      return (
        <TranslationPanel lang={select.slice(9)} routeState={routeState} />
      );
    }
    if (select === "rprop") {
      return (
        <RoutePropsPanel
          routeState={routeState}
          routeDispatch={routeDispatch}
          currentLanguage={currentLanguage}
        />
      );
    }

    // pois variants
    if (select.startsWith("pois")) {
      if (select === "pois") {
        return (
          <POIsListPanel
            routeState={routeState}
            currentLanguage={currentLanguage}
          />
        );
      }
      const no = GetIndexInArray(
        Number(select.slice(4)),
        routeState.pointsOfInterest
      );
      if (no === -1) {
        return (
          <div className="p-3 text-sm text-muted-foreground">Loading…</div>
        );
      }
      return (
        <POIPanel
          no={no}
          routeState={routeState}
          routeDispatch={routeDispatch}
          currentLanguage={currentLanguage}
        />
      );
    }

    // bss variants
    if (select.startsWith("bss")) {
      if (select === "bss") {
        return <BackgroundSoundsListPanel routeState={routeState} />;
      }
      const no = GetIndexInArray(
        Number(select.slice(3)),
        routeState.backgroundSounds
      );
      return (
        <BackgroundSoundPanel
          no={no}
          routeState={routeState}
          routeDispatch={routeDispatch}
        />
      );
    }

    // fss variants
    if (select.startsWith("fss")) {
      if (select === "fss") {
        return <FocusedSoundsListPanel routeState={routeState} />;
      }
      const no = GetIndexInArray(
        Number(select.slice(3)),
        routeState.focusedSounds
      );
      return (
        <FocusedSoundPanel
          no={no}
          routeState={routeState}
          routeDispatch={routeDispatch}
        />
      );
    }

    // rline variants
    if (select.startsWith("rline")) {
      if (select === "rline") {
        return <RouteLineListPanel routeState={routeState} />;
      }
      const no = Number(select.slice(5));
      return <RouteLinePointPanel no={no} routeState={routeState} />;
    }

    return null;
  }

  return (
    <div className={styles.container} key={`${select}-${currentLanguage}`}>
      <ScrollArea className="h-full w-full">{renderPanel()}</ScrollArea>
    </div>
  );
};
